import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from "react-redux";
import moment from 'moment';
import dayjs from 'dayjs';
import tobeor from 'tobeor';
import clsx from 'clsx';

import { Button, ButtonBase } from '@mui/material';
import { makeStyles } from '@mui/styles';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EventIcon from "@mui/icons-material/Event";

import PredicateChain from './PredicateChain';
import ArrowTooltipCss from './ArrowTooltipCss';

import Node from './Node';
import Search from './Search';
import CommonStyles from './CommonStyles';
import LoaderNew from './LoaderNew';
import TrplSelect from './TrplControls/TrplSelect';
import TrplCheckbox from './TrplControls/TrplCheckbox';
import TrplTextfield from './TrplControls/TrplTextfield';

import { updateLightMetaAndOps } from '../reducer/metaReducer';

import { LocalizationProvider, DesktopDatePicker} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'

import {
  getLastRstData,
  generateFilter,
  getFilterPidsChain,
  getFilterReadyForBackend,
  getFilterReadyForFrontend,
  getFilterRstTitle,
  isNotPlaceholderFilter,
  isNotSearchFilter,
  prdcParamsTable,
} from '../utils';

import { NODE_TITLE, TRPL_SELECT_PLACEHOLDER } from '../constants';

const useStyles = makeStyles({
  emptyFiltersText: {
    whiteSpace: 'nowrap',
    marginTop: '4px',
    padding: '3px',
    marginBottom: '13px',
    color: '#a2abc2',
  },
  filterDatePicker: {
    height: '24px',
    fontSize: '12px',
    paddingLeft: '5px',
    boxSizing: 'border-box',
    backgroundColor: '#e8ebf5',
  },
  conSelectContainer: {
    width: '59px',
    flexShrink: '0',
    marginRight: '5px',
  },
  rstSelectContainer: {
    width: '190px',
    flexShrink: '0',
    '& .MuiSelect-select': {
      '&:focus': {
        background: '#e8ebf5',
      },
    },
  },
  addFilterContainer: {
    display: 'flex',
    marginTop: '16px',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  addFilterBtn: {
    color: '#4C4C4C',
    display: 'inline-flex',
    '&:hover': {
      cursor: 'pointer',
    },
    marginTop: '5px',
    marginBottom: '5px',
  },
  setFiltersBtn: {
    borderRadius: '15px',
    '&:hover': {
      '& .filters-loader div': {
        backgroundColor: '#fff',
      },
    },
  },
  filtersInput: {
    width: '100%',
    height: '23px',
    border: 'none',
    minWidth: '80px',
    paddingLeft: '5px',
    background: '#e8ebf5',
    boxSizing: 'border-box',
    fontSize: '12px',
    '&:hover': {
      background: '#DBD9E7',
    },
    '&:focus': {
      background: '#DBD9E7',
    },
  },
  functionButton: {
    flexShrink: '0',
    width: '24px',
    height: '24px',
    textTransform: 'none',
    backgroundColor: 'white',
    color: '#C4C4C4',
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: '12px',
    lineHeight: '16px',
    marginLeft: '8px',
    '&:hover': {
      color: '#4C4C4C',
    },
  },
  textFieldInput: {
    border: 'none',
    outline: 'none',
    height: '24px',
    fontSize: '12px',
    padding: '5px',
    color: '#4E4C4C',
    background: '#e8ebf5',
    '&:hover': {
      background: '#eeecf8',
    },
    '&:focus': {
      background: '#DBD9E7',
    },
  },
  enabledFunctionButton: {
    backgroundColor: '#E0EFF3',
    color: '#347CFF',
  },
  functionButtonHasValue: {
    color: '#347CFF',
  },
  valuesContainer: {
    marginLeft: '5px',
    width: '-webkit-fill-available',
  },
  ...CommonStyles,
});

function EventIconSx() {
  return <EventIcon sx={{ fontSize: "18px", color: '#8589AC', marginRight: '-8px' }} />;
}

const FilterRow = ({
  rst,
  mode,
  nodes,
  filter,
  isEvent,
  classes,
  callbacks,
  inputProps,
  filterIndex,
  removeFilter,
  copyFilterRow,
  onFilterChange,
  rstSelectValues,
  onApplyFiltersClick,
  graphTriggerAncestor,
  setSearchInputFocused,
}) => {
  const inputRef = useRef(null);

  const changeShowFunctionField = event => {
    event.preventDefault();
    onFilterChange(filterIndex, 'showFunc', !filter.showFunc);
  };

  const updateFunction = event => {
    onFilterChange(filterIndex, 'func', event.target.value);
  };

  const setTime = (time) => {
    if (time && time.$d && time.$d.getTime()) {
      const date = time.utcOffset(0).set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).$d.getTime();

      onFilterChange(filterIndex, 'val', date);
    }
  };

  useEffect(() => {
    if (rst && rst.type === 'BOOLEAN' && filter.val == null) {
      onFilterChange(filterIndex, 'val', false);
    }
    
    if (inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  }, [filter.pids_chain, filter.showFunc]);

  const filterRstTitle = getFilterRstTitle(filter);

  return (
    <div
      className={classes.alignCenter}
      style={{
        marginBottom: '5px',
        backgroundColor: 'ffff',
      }}
    >
      {(!mode || mode === 'edit') &&
      <CloseIcon
        className={classes.pointer}
        style={{
          fontSize: '17px',
          color: '#7978A8',
          marginRight: '10px',
          '&:hover': {
            color: '#4c4c4c',
          },
        }}
        onClick={() => removeFilter(filterIndex)}
      />}

      <div className={classes.conSelectContainer}>
        {filterIndex === 0 ?
          <span>
            {isEvent ? 'When' : 'Where'}
          </span>
          :
          <TrplSelect
            disabled={mode === 'show'}
            margin="none"
            complicatedValues
            values={[{ title: 'And', value: 'AND' }, { title: 'Or', value: 'OR' }]}
            value={filter.con || rst.filter_ops[0]}
            onChange={(e) => {
              onFilterChange(filterIndex, 'con', e.target.value);
            }}
          />
        }
      </div>

      {graphTriggerAncestor ? (
        <div>
          <PredicateChain
            mode={mode}
            inputRef={inputRef}
            rst={filter.pids_chain}
            ancestor={graphTriggerAncestor.id}
            addInnerRstCb={({ newSelected }) => {
              onFilterChange(filterIndex, 'pids_chain', newSelected);
            }}
            onRemoveRstCb={( props, newSelected ) => {
              onFilterChange(filterIndex, 'pids_chain', newSelected);
            }}
          />
        </div>
        ) : (
        <div className={classes.rstSelectContainer}>
          <TrplSelect
            fullWidth
            margin="none"
            complicatedValues
            value={filterRstTitle}
            values={rstSelectValues}
            onChange={(e) => {
              onFilterChange(filterIndex, 'pids_chain', getFilterPidsChain(e.target.value));
            }}
          />
        </div>
      )}

      {rst &&
        <div className={classes.ml5}>
          <TrplSelect
            disabled={mode === 'show'}
            margin="none"
            values={rst.filter_ops}
            value={filter.op || rst.filter_ops[0]}
            onChange={(e) => {
              onFilterChange(filterIndex, 'op', e.target.value);
            }}
          />
        </div>
      }

      {filter.op === '' ? null :
        <React.Fragment>
          {!filter.showFunc && isNotPlaceholderFilter(filter) && filter.pids_chain ? (
            <div className={classes.valuesContainer}>
              {rst && rst.values && rst.primitive !== '_obj' ? (
                <TrplSelect
                  disabled={mode === 'show'}
                  margin="none"
                  placeholder='Value'
                  values={rst.values}
                  value={filter.val || 'Value'}
                  onChange={(e) => {
                    onFilterChange(filterIndex, 'val', e.target.value);
                  }}
                />
              ) : rst && rst.primitive === '_bool' ? (
                <TrplCheckbox
                  disabled={mode === 'show'}
                  value={filter.val}
                  checked={typeof filter.val === 'boolean' ? filter.val : filter.val === 'true'}
                  style={{
                    height: '24px',
                    marginRight: '-11px',
                  }}
                  onChange={(e) => {
                    onFilterChange(filterIndex, 'val', e.target.checked);
                  }}
                />
              ) : rst && tobeor(rst.type, ['DATE', 'DATE_AND_TIME']) ? (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DesktopDatePicker
                    disabled={mode === 'show'}
                    components={{ OpenPickerIcon: EventIconSx }}
                    value={dayjs(Number(filter.val))}
                    onChange={setTime}
                    transitionprops={inputRef}
                    slotProps={{ textField: { variant: 'outlined', InputProps: inputProps } }}
                  />
                </LocalizationProvider>
                ) : filterRstTitle && rst && rst.primitive === '_obj' ? (
                  <React.Fragment>
                    {filter.val ? (
                      <Node
                        isLink
                        size="sm"
                        isRemovable={!mode || mode === 'edit'}
                        nodeId={filter.val}
                        node={nodes[filter.val]}
                        nodes={nodes}
                        onRemoveClick={() => onFilterChange(filterIndex, 'val', '')}
                      />
                    ) : (
                        (!mode || mode === 'edit') &&
                        <Search
                          ancestorId={rst.oid}
                          predefinedResults={rst.values || null}
                          containerStyles={{
                            width: '100%',
                            height: '24px',
                            minWidth: '100px',
                          }}
                          onInputBlur={() => {
                            setSearchInputFocused(false);
                          }}
                          onInputFocus={() => {
                            setSearchInputFocused(true);
                          }}
                          inputStyles={{
                            fontSize: '12px',
                          }}
                          inputRefFunc={inputRef}
                          onDropdownElementClick={({ node }) => {
                            if (callbacks.onFiltersSearchNodeCb) callbacks.onFiltersSearchNodeCb(node);

                            onFilterChange(filterIndex, 'val', node.id);
                          }}
                        />
                      )
                    }
                    </React.Fragment>
                  ) : (
                      <TrplTextfield
                        disabled={mode === 'show'}
                        inputRef={inputRef}
                        fullWidth
                        margin="none"
                        InputProps={{ classes: {input: classes.filtersInput} }}
                        type={rst && tobeor(rst.primitive, ['_int', '_float']) ? 'number' : 'text'}
                        onChange={
                          (e) => onFilterChange(filterIndex, 'val', e.target.value)
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') onApplyFiltersClick(e);
                        }}
                        value={filter.val || ''}
                      />
                  )}
            </div>
          ) : null}

          {filter.showFunc ?
            <div className={classes.valuesContainer}>
              <input
                disabled={mode === 'show'}
                ref={inputRef}
                value={filter.func || ''}
                className={`${classes.filtersInput}`}
                onChange={updateFunction}
              />
            </div>
          : null}

          {rst &&
            <React.Fragment>
              <button
                disabled={mode === 'show'}
                onClick={changeShowFunctionField}
                type="button"
                className={clsx(
                  classes.functionButton,
                  filter.showFunc && classes.enabledFunctionButton,
                  !!filter.func && classes.functionButtonHasValue
                )}
              >
                fn
              </button>
            </React.Fragment>
          }
        </React.Fragment>
      }

      {(!mode || mode === 'edit') &&
        <ArrowTooltipCss text="Duplicate filter row">
          <ButtonBase
            disabled={mode === 'show'}
            onClick={() => copyFilterRow(filterIndex)}
            className={`${classes.btnEmpty}`}
            style={{
              marginLeft: '5px',
            }}
          >
            <ContentCopyIcon
              style={{
                fontSize: '15px',
                padding: '5px',
                background: '#ffff',
              }}
            />
          </ButtonBase>
        </ArrowTooltipCss>
      }
    </div>
  );
};

export default React.memo(
  ({
    mode,
    nodes,
    loaded,
    isEvent,
    ancestor,
    useChain,
    defaultBg,
    rstDataMap,
    filterBtnText,
    initialFilters,
    callbacks = {},
    initialSearchValue,
    graphTriggerAncestor,
    notEditableGraphTrigger,
  }) => {
    const classes = useStyles();
    const [filterAncestor, setFilterAncestor] = useState(graphTriggerAncestor);
    const [filters, setFilters] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const [searchInputFocused, setSearchInputFocused] = useState(false);
    const [rstSelectValues, setRstSelectValues] = useState(
      // filterByNodeTitle
      //   ? [
      //       {
      //         title: 'Node title',
      //         value: NODE_TITLE,
      //         style: { fontStyle: 'italic' },
      //       },
      //     ]
      //   : []
    );

    const dispatch = useDispatch();
    const light_meta_and_ops = useSelector((state) => state.meta.light_meta_and_ops);

    useEffect(() => {
      // first of all - collect values from rstData for each filter rst select
      const rstSelectValuesCopy = [];
      if (JSON.stringify(light_meta_and_ops) === '{}') dispatch(updateLightMetaAndOps());

      if (rstDataMap)
      for (const rst of rstDataMap.values()) {
        if (rst && rst.type && rst.type === 'BUTTON') continue;

        const chainTitle = rst.chainTitle ? rst.chainTitle : rst.id;
        const title = rst.complicated_title ? rst.complicated_title : rst.title;

        const value = { title: `${ title + ' [' + chainTitle + ']'}`, value: chainTitle, rst };

        rstSelectValuesCopy.push(value);
      }

      // and of course set it to state. Without mutations, of course
      setRstSelectValues(rstSelectValuesCopy);

      if (initialFilters && initialFilters.length) setFilters(initialFilters.map(getFilterReadyForFrontend));
      if (initialSearchValue) setSearchValue(initialSearchValue);
    }, [mode, light_meta_and_ops]);

    const addFilter = () => {
      let filtersCopy;
      if (useChain) filtersCopy = [...filters, generateFilter()];
      if (!useChain) filtersCopy = [...filters, generateFilter(TRPL_SELECT_PLACEHOLDER)];

      setFilters(filtersCopy);

      const cleanedFilters = filtersCopy.map(getFilterReadyForBackend);

      if (callbacks.setFiltersToParentState) callbacks.setFiltersToParentState(cleanedFilters);
    };

    const removeFilter = (index) => {
      const filter = filters[index];

      const isSearchFilter = !isNotSearchFilter(filter);

      const filtersCopy = [...filters];

      filtersCopy.splice(index, 1);

      setFilters(filtersCopy);

      const cleanedFilters = filtersCopy.map(getFilterReadyForBackend);

      if (callbacks.setFiltersToParentState) callbacks.setFiltersToParentState(cleanedFilters);
      if (!filtersCopy.length && callbacks.onFilterBtnClick) {
        callbacks.onFilterBtnClick([], '');
      }

      if (isSearchFilter) setSearchValue('');
    };

    const getCurrentTimeInFilter = () => {
      const todayByUTC = moment(new Date())
        .utcOffset(0)
        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        ._d.getTime();

      return todayByUTC;
    }

    const getRstWithOperators = (filter) => {
      let rstFull;

      if (light_meta_and_ops && light_meta_and_ops.setup_rbox_light) {
        rstFull = getLastRstData(filter.pids_chain, ancestor, light_meta_and_ops.setup_rbox_light, prdcParamsTable);
        if (rstFull && !rstFull.filter_ops) {
          const operators = light_meta_and_ops && light_meta_and_ops.predicates_ops;
          const rstPrimitive = rstFull.primitive;
          rstFull.filter_ops = operators[rstPrimitive] || ['='];
        }
        if (graphTriggerAncestor && rstFull && rstFull.filter_ops && rstFull.filter_ops[0] !== '') {
          rstFull.filter_ops.unshift('');
        }
      }
      if (rstFull && rstFull.bounds && rstFull.bounds.length) {
        const rstValues = [];
        for (const item of rstFull.bounds) {
          Object.values(item).forEach(itemValue => {
            if (!Array.isArray(itemValue)) rstValues.push(itemValue);
          });
        }
        rstFull.values = rstValues;
      }
      return rstFull;
    }

    const onFilterChange = (index, key, value) => {
      const filter = { ...filters[index] };

      if (isNotSearchFilter(filter)) {
        if (key === 'val') {
          // I really don't understand where it can be used
          //UPD: it's for filtering by table node titles, TODO fix
          if (value === NODE_TITLE) {
            filter.pids_chain = [NODE_TITLE];
          } else {
            filter.val = '';
          }
        }
        if (key === 'showFunc' && value && !filter.hasOwnProperty('func')) {
          filter.func = ''
        }
      } else {
        setSearchValue(value);
      }

      if (value !== '') filter[key] = value;
      if (filter[key] === '') delete filter[key];

      if (key === 'pids_chain') {
        delete filter.val;
        delete filter.func;
        delete filter.showFunc;
        delete filter.op;

        // TODO fix duplication logic on rstAddFilterClick on TrplsTable

        // if rst is DATE or DATE_AND_TIME type - set value on rst select change
        // because you need to show Apply button
        let rstFull = getRstWithOperators(filter);
        if (rstFull) {
          if (tobeor(rstFull.type, ['DATE', 'DATE_AND_TIME', 'TIME'])) {
            filter.val = getCurrentTimeInFilter();
          } else if (rstFull.primitive === '_bool') {
            filter.val = false;
          }
          if (rstFull.filter_ops) filter.op = rstFull.filter_ops[0];
        }

      } else if (((key === 'op' && value) || (key === 'showFunc' && value === false)) && !filter.val) {

        let rstFull = getRstWithOperators(filter);
        if (rstFull && tobeor(rstFull.type, ['DATE', 'DATE_AND_TIME', 'TIME'])) {
          filter.val = getCurrentTimeInFilter();
        }
      }

      // replace filter obj in array
      const filtersCopy = [...filters];
      filtersCopy[index] = filter;

      setFilters(filtersCopy);
      const cleanedFilters = filtersCopy.map(getFilterReadyForBackend);

      if (callbacks.setFiltersToParentState) callbacks.setFiltersToParentState(cleanedFilters, searchValue);
    };

    const isFilterHasPredicateAndOperator = (filter) => {
      const filledValue = (filter.pids_chain && filter.con) && (filter.pids_chain[0] !== "trpl-select-placeholder");
      return (filledValue);
    };

    const allFiltersWithPredicates = filters.length && filters.every(isFilterHasPredicateAndOperator);

    const onApplyFiltersClick = (e) => {
      e.preventDefault();

      if (searchInputFocused) return;

      // if filters have items without predicate and/or operator - ignore submit
      if (!filterAncestor && !allFiltersWithPredicates) return;

      const cleanedFilters = filters.map(getFilterReadyForBackend);

      if (callbacks.onFilterBtnClick) {
        callbacks.onFilterBtnClick(cleanedFilters, searchValue, filterAncestor);
      }
    };

    const onGraphTriggerAncestorClick = (node) => {
      setFilterAncestor(node);
      if (callbacks.setGraphTriggerAncestorToParentState) callbacks.setGraphTriggerAncestorToParentState(node);
    };

    const copyFilterRow = (index) => {
      const filter = filters[index];
      const filtersCopy = [...filters];

      filtersCopy.splice(index, 0, {...filter});

      setFilters(filtersCopy);

      const cleanedFilters = filtersCopy.map(getFilterReadyForBackend);

      if (callbacks.setFiltersToParentState) callbacks.setFiltersToParentState(cleanedFilters);
    };

    const onRemoveGraphTriggerAncestorClick = () => {
      setFilterAncestor(null);
      setFilters([]);
      if (callbacks.setFiltersToParentState) callbacks.setFiltersToParentState([]);
      if (callbacks.setGraphTriggerAncestorToParentState) callbacks.setGraphTriggerAncestorToParentState(null);
    };

    const nodesData = (!nodes || !Object.values(nodes).length) && light_meta_and_ops.setup_rbox_light
      ? light_meta_and_ops.setup_rbox_light : nodes;

    return (
      <div className={classes.p10} style={{ backgroundColor: defaultBg ? '#ffff' : '', borderRadius: '12px' }}>
        {useChain && <div className={classes.filterContainer}>

          <div className={`${classes.filterHeading} ${classes.mb10}`}>
            {filterAncestor ? (
              <Node
                isLink
                size="sm"
                nodes={nodesData}
                isRemovable={mode === 'edit' && !notEditableGraphTrigger}
                node={filterAncestor}
                onRemoveClick={onRemoveGraphTriggerAncestorClick}
              />
            ) : (
              <Search
                showConcepts
                conceptsOnly={true}
                containerStyles={{
                  height: 24,
                }}
                onDropdownElementClick={({ node }) => {
                  onGraphTriggerAncestorClick(node);
                  if (callbacks.onFiltersSearchNodeCb) callbacks.onFiltersSearchNodeCb(node);
                }}
              />
            )}
          </div>
        </div>}

        {!filters.length ? (
          <div className={classes.emptyFiltersText}>No filters applied to this view yet</div>
        ) : (
          filters.map((filter, filterIndex) => {
            const inputProps = {
              classes: {
                input: `${classes.textFieldInput} ${classes.filterDatePicker}`,
              },
            };

            const filterRstTitle = getFilterRstTitle(filter);

            let rstFull = getRstWithOperators(filter);

            return (
              <FilterRow
                useChain
                mode={mode}
                rst={rstFull}
                filter={filter}
                nodes={nodesData}
                classes={classes}
                isEvent={isEvent}
                callbacks={callbacks}
                inputProps={inputProps}
                filterIndex={filterIndex}
                removeFilter={removeFilter}
                copyFilterRow={copyFilterRow}
                onFilterChange={onFilterChange}
                rstSelectValues={rstSelectValues}
                graphTriggerAncestor={filterAncestor}
                key={`${filterIndex}-${filterRstTitle}`}
                onApplyFiltersClick={onApplyFiltersClick}
                setSearchInputFocused={setSearchInputFocused}
              />
            );
          })
        )}

        <div className={classes.addFilterContainer}>
          {(!mode || (mode ==='edit' && filterAncestor)) &&
            <div onClick={addFilter} className={classes.addFilterBtn}>
              <AddIcon style={{ fontSize: '15px', marginRight: '6px' }} />{' '}
              <span style={{ lineHeight: '15px' }}>Add filter</span>
            </div>
          }

          {(allFiltersWithPredicates || filterAncestor) && filterBtnText ? (
            <Button
              size="small"
              disabled={!loaded}
              style={{
                textTransform: 'capitalize',
              }}
              type="submit"
              onClick={onApplyFiltersClick}
              className={`${classes.btnNormal} ${classes.setFiltersBtn}`}
            >
              {loaded !== undefined && loaded === false ? (
                <LoaderNew style={{ height: '17px' }} className="filters-loader" />
              ) : (
                filterBtnText
              )}
            </Button>
          ) : null}
        </div>
      </div>
    );
  }
);
